import React, {
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useCallback,
  useImperativeHandle,
} from 'react'
import * as Yup from 'yup'
import PropTypes from 'prop-types'
import { useFormik } from 'formik'
import { isEqual, isEmpty } from 'lodash'
import ReactTooltip from 'react-tooltip'

import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Input from '@material-ui/core/Input'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import FormControl from '@material-ui/core/FormControl'
import InputAdornment from '@material-ui/core/InputAdornment'
import FormHelperText from '@material-ui/core/FormHelperText'

import makeStyles from '@material-ui/core/styles/makeStyles'

import Autocomplete from '@material-ui/lab/Autocomplete'
import SearchIcon from '@material-ui/icons/Search'

import { usePrice } from '../../../../../shared/contexts/PriceContext'
import useYup from '../../../../../shared/hooks/useYup'
import ConfidenceTooltip from '../../ConfidenceTooltip'
import useReceitaWSClient from '../../../../../clients/ReceitaWSClient/useReceitaWSClient'

import {
  useAlert,
  useLoader,
  ButtonLight,
  ButtonGradient,
  CPFOrCNPJInput,
  CNPJ_SIZE_WITHOUT_MASK,
} from '../../../../../shared/components'
import { GlobalMessages } from '../../../../../shared/messages'
import CollapsePrice from '../../../../../shared/components/CollapsePrice/CollapsePrice'
import useUserClient from '../../../../../clients/UserClient/useUserClient'
import InsuredData from '../../../../../models/PerformanceModels/InsuredData'

const useStyles = makeStyles((theme) => ({
  buttonsStep: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& .MuiBox-root': {
      minWidth: 190,
      [theme.breakpoints.down('xs')]: {
        marginTop: 5,
        marginLeft: 5,
        minWidth: 115,
      },
    },
  },
  label: {
    color: theme.palette.text.quaternary,
    fontWeight: 'bold',
  },
  labelNoBadge: {
    color: theme.palette.text.quaternary,
    fontWeight: 'bold',
    marginTop: '5px',
  },
  help: {
    color: theme.palette.text.quaternary,
  },
  noOptionsLabel: {
    fontSize: 14,
    color: '#c93952',
    fontWeight: 'bold',
  },
}))

const Insured = forwardRef((props, ref) => {
  const classes = useStyles()
  const userClient = useUserClient()

  const {
    quote,
    quoteAiData,
    documentId,
    checked,
    handleStep,
    handleNext,
    handlePrevious,
  } = props
  const { cpfOrCnpj } = useYup()
  const { showLoader, hideLoader } = useLoader()
  const { addMsgDanger } = useAlert()
  const receitaWSClient = useReceitaWSClient()

  const {
    guaranteePerformance,
    actionNext,
    setFormikChanged,
    setGuaranteePerformance,
    onNext,
  } = usePrice()

  const [hasNoneClient, setHasNoneClient] = useState(false)
  const [policiesHolder, setPoliciesHolder] = useState([{}])
  const [hasUniqueClient, setHasUniqueClient] = useState(true)
  const [claimantDisabled, setClaimantDisabled] = useState(true)
  const [policyHolderSelected, setPolicyHolderSelected] = useState('')
  const [policyHolderOptions, setPolicyHolderOptions] = useState([''])

  const showConfidenceToolTip = quoteAiData && quoteAiData.status === 'Processed' && quoteAiData.confidences

  const initialValuesForm = useMemo(() => (Object.assign(
    InsuredData(quote?.Insured ?? {}),
  )), [quote?.Insured])

  const policyHolderCnpj = useMemo(() => (
    policyHolderSelected?.replace(/[.\-/\s]/g, '') ?? ''
  ), [policyHolderSelected])

  const fieldValueIquals = () => (cpfOrCnpj
    .test('document', GlobalMessages.EQUAL_TO_POLICY_HOLDER,
      (value) => (
        value && value.replace(/[.\-/\s]/g, '') !== policyHolderCnpj
      ))
    .required())

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValuesForm,
    validationSchema: Yup.object({
      document: fieldValueIquals(),
      name: !claimantDisabled ? Yup.string()
        .matches(/^[A-Za-z\s]+$/, 'Apenas letras são permitidas').required('Campo obrigatório') : Yup.string().required(),
      policyHolder: Yup.string().max(200)
        .when(() => (hasNoneClient ? Yup.string() : Yup.string().required())),
    }),
    onSubmit: (data) => {
      const values = {
        ...guaranteePerformance,
        ...{ insured: { ...data } },
      }

      const {
        name,
        document,
      } = values.insured

      const valueInsuredObject = {
        name,
        document,
      }

      userClient().getClientByUserLogged().then((response) => {
        const filterNotLead = response?.data?.filter((item) => item?.lead === false)
        const filterNomination = filterNotLead
          ?.filter((item) => item?.cpfcnpj === values.insured.documentNumber)
        if (filterNomination?.length > 0) {
          setGuaranteePerformance({ ...values, ClientIsNominated: true })
        } else {
          setGuaranteePerformance({ ...values, ClientIsNominated: false })
        }
      }, () => {
        setGuaranteePerformance({ ...values })
      })

      if (actionNext) {
        setFormikChanged(!isEqual(initialValuesForm, data))
        onNext(values)
        if (valueInsuredObject.document?.replace(/[.\-/\s]/g, '')?.length !== CNPJ_SIZE_WITHOUT_MASK) {
          userClient().createUpdateInsuredObjectProperty(valueInsuredObject)
            .then((response) => {
              addMsgDanger(response.data.error)
            })
        }
      }
    },
  })

  useImperativeHandle(ref, () => ({
    onSubmit: () => new Promise((resolve) => {
      formik.validateForm().then((errors) => {
        let data

        if (!isEmpty(errors)) {
          data = { message: GlobalMessages.VERIFIQUE_CAMPOS_DESTAQUE_ANTES_PROSSEGUIR }
        }

        resolve(data)
        formik.submitForm()
      })
    }),
  }))

  const formatClients = useCallback((clientsUser) => clientsUser.map(({ name, cpfcnpj }) => ({
    id: cpfcnpj,
    text: `${cpfcnpj} ${name}`,
  })), [])

  const { setValues } = formik

  useEffect(() => {
    const isAiFlow = !!documentId
    const aiFinishedLoading = !isAiFlow || quoteAiData?.status === 'Processed'

    if (!aiFinishedLoading) {
      return
    }

    userClient().getClientByUserLogged(true).then((response) => {
      const filterNotLead = response?.data?.filter((item) => item?.lead === false)
      const clientsData = formatClients(filterNotLead
        ?.filter(({ cpfcnpj }) => cpfcnpj?.replace(/[.\-/\s]/g, '')?.length === CNPJ_SIZE_WITHOUT_MASK))

      setHasNoneClient(clientsData.length === 0)

      if (clientsData.length !== 0) {
        setHasUniqueClient(false)
        setPoliciesHolder(clientsData)
        setPolicyHolderOptions(clientsData.map(({ text }) => text))

        const shouldAutoFill = clientsData.length === 1 && !isAiFlow

        if (shouldAutoFill) {
          const { text: nameClient, id: documentPolicy } = clientsData[0]

          setValues({
            policyHolder: nameClient,
            documentNumber: documentPolicy?.replace(/[.\-/\s]/g, ''),
          })
          setHasUniqueClient(true)
        } else {
          setHasUniqueClient(false)
        }
      }
    })
  }, [
    setValues,
    userClient,
    formatClients,
    setHasUniqueClient,
    quoteAiData?.status,
    documentId,
  ])

  const handleInfoCnpj = (evento) => {
    const { value } = evento.target

    const normalized = value?.replace(/[.\-/\s]/g, '') ?? ''

    if (normalized.length === CNPJ_SIZE_WITHOUT_MASK) {
      showLoader()
      receitaWSClient().getInfoByCnpj(normalized).then(async (response) => {
        if (response?.data?.nome) {
          formik.setFieldValue('name', response.data.nome)
          setClaimantDisabled(true)
        } else {
          setClaimantDisabled(false)
        }
        hideLoader()
      }).catch(() => {
        setClaimantDisabled(false)
        hideLoader()
      })
    } else {
      showLoader()
      setClaimantDisabled(false)
      userClient().getClientInsuredObjectProperty(normalized).then(async (response) => {
        if (response) {
          formik.setFieldValue('name', response?.data?.nome ?? '')
          hideLoader()
        }
      }).catch(() => {
        hideLoader()
      })
    }
  }

  const handlePolicyHolder = useCallback((value) => {
    const policiesHolderFound = policiesHolder.find((item) => item?.text === value)
    if (!isEmpty(policiesHolderFound)) {
      formik.setFieldValue('documentNumber', policiesHolderFound?.id)
    }
  }, [formik, policiesHolder])

  const placeholderCPFCNPJ = '000.000.000-00 / 00.000.000/0000-00'

  const nameLabel = 'Segurado'
  const policyHolderLabel = 'Tomador'
  const cpfCnpjLabel = 'CPF/CNPJ do Segurado'

  const noOptionsMessage = (
    <Typography className={classes.noOptionsLabel}>
      {GlobalMessages.NO_OPTIONS_MESSAGE}
    </Typography>
  )

  return (
    <>
      <CollapsePrice
        checked={checked}
        handleEdit={handleStep}
        hiddenEdit={isEmpty(quote?.Insured) && !formik.isSubmitting}
        title="Dados do Segurado"
        subtitle="Informe abaixo os dados do segurado"
      >
        <Grid container spacing={5}>
          <Grid item sm={12} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="policyHolderLabel" className={classes.label}>
                {policyHolderLabel}
                {showConfidenceToolTip && quoteAiData.policyHolders[0]?.document !== '' && (
                  <ConfidenceTooltip
                    text={quoteAiData.sources?.policyHolderDocument?.imageText}
                    imageUrl={quoteAiData.sources?.policyHolderDocument?.imageUrl}
                    confidence={quoteAiData.confidences.policyHolders[0].document}
                  />
                )}
              </label>
              <Autocomplete
                options={policyHolderOptions}
                disabled={hasUniqueClient}
                noOptionsText={noOptionsMessage}
                getOptionLabel={(option) => option ?? ''}
                onInputChange={(e, value) => {
                  if (e?.type === 'click') {
                    formik.setFieldValue('policyHolder', value)
                    handlePolicyHolder(value)
                    setPolicyHolderSelected(value)
                  }
                }}
                getOptionSelected={(option, value) => option === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    onFocus={(event) => event.target.setAttribute('autocomplete', 'none')}
                    placeholder="Procurar"
                    margin="normal"
                    error={formik.touched.policyHolder && !!formik.errors.policyHolder}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                {...formik.getFieldProps('policyHolder')}
              />
              <FormHelperText
                hidden={!formik.touched.policyHolder || !formik.errors.policyHolder}
                error={formik.touched.policyHolder && !!formik.errors.policyHolder}
              >
                {formik.errors.policyHolder}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={5}>
              <Grid item sm={6} xs={12}>
                <FormControl fullWidth>
                  <label htmlFor="cpfCnpjLabel" className={(!showConfidenceToolTip || quoteAiData.sources?.insuredDocument?.imageText) ? classes.label : classes.labelNoBadge}>
                    {cpfCnpjLabel}
                    {showConfidenceToolTip && quoteAiData.sources?.insuredDocument?.imageText && (
                      <ConfidenceTooltip
                        text={quoteAiData.sources?.insuredDocument?.imageText}
                        imageUrl={quoteAiData.sources?.insuredDocument?.imageUrl}
                        confidence={quoteAiData.confidences.insureds[0].document}
                      />
                    )}
                  </label>
                  <CPFOrCNPJInput
                    fullWidth
                    id="document"
                    color="secondary"
                    placeholder={placeholderCPFCNPJ}
                    {...formik.getFieldProps('document')}
                    error={formik.touched.document && !!formik.errors.document}
                    onBlur={handleInfoCnpj}
                  />
                  <FormHelperText
                    hidden={!formik.touched.document || !formik.errors.document}
                    error={formik.touched.document && !!formik.errors.document}
                  >
                    {formik.errors.document}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item sm={6} xs={12}>
                <FormControl fullWidth>
                  <label htmlFor="nameLabel" className={classes.label}>
                    {nameLabel}
                  </label>
                  <Input
                    fullWidth
                    id="name"
                    color="secondary"
                    placeholder="Nome do Segurado"
                    disabled={claimantDisabled}
                    {...formik.getFieldProps('name')}
                    error={formik.touched.name && !!formik.errors.name}
                  />
                  <FormHelperText
                    hidden={!formik.touched.name || !formik.errors.name}
                    error={formik.touched.name && !!formik.errors.name}
                  >
                    {formik.errors.name}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.buttonsStep}>
              <Box>
                <ButtonLight
                  onClick={handlePrevious}
                  title="Voltar"
                >
                  Voltar
                </ButtonLight>
              </Box>
              <Box ml={4}>
                <ButtonGradient
                  onClick={handleNext}
                  title="Continuar"
                >
                  Continuar
                </ButtonGradient>
              </Box>
            </Box>
            <ReactTooltip place="top" type="dark" />
          </Grid>
        </Grid>
      </CollapsePrice>
    </>
  )
})

Insured.propTypes = {
  quote: PropTypes.any,
  quoteAiData: PropTypes.any,
  documentId: PropTypes.string,
  checked: PropTypes.bool,
  handleStep: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  handlePrevious: PropTypes.func.isRequired,
}

Insured.defaultProps = {
  checked: false,
  quote: undefined,
  quoteAiData: undefined,
  documentId: undefined,
}

export default Insured
